import { html, render, nothing, type TemplateResult } from 'lit-html';
import styles from './pk-key-detail.css?inline';
import { formatUtc } from '../../lib/portal-data.js';
import {
    custodyLabel, keyName, kindLabel, parseJsonRecord, stateKind, stateLabel,
    type PkKeyDetailRecord, type PkKeyImpact,
} from '../../lib/keys.js';
import '../pk-key-value-grid/pk-key-value-grid.js';
import '../pk-status-pill/pk-status-pill.js';

/** One-line text of an impact: `3 objects · epochs 1, 2 · data classes attachment, message · wraps 1 key`. */
export function impactSummary(impact: PkKeyImpact): string {
    const parts = [`${impact.objectCount} object${impact.objectCount === 1 ? '' : 's'}`];
    if (impact.epochs && impact.epochs.length > 0) parts.push(`epoch${impact.epochs.length === 1 ? '' : 's'} ${impact.epochs.join(', ')}`);
    if (impact.dataClasses && impact.dataClasses.length > 0) parts.push(`data class${impact.dataClasses.length === 1 ? '' : 'es'} ${impact.dataClasses.join(', ')}`);
    const deps = impact.dependents?.length ?? 0;
    if (deps > 0) parts.push(`wraps ${deps} key${deps === 1 ? '' : 's'}`);
    return parts.join(' · ');
}

/**
 * `<pk-key-detail>` — one key: a header with its custody and state pills, the metadata grid
 * (`<pk-key-value-grid>`), the impact summary (what the key protects), the last audit events, and an action bar
 * (the `actions` slot — the page puts `pk-destroy-key-flow`, the escrow forms, … there).
 *
 * Attributes:
 *   - `key`         — JSON of the key (contract `KeyDetail`, with `audit`); the `key` property wins once set.
 *   - `impact`      — JSON of the impact (contract `KeyImpact`); absent = "Impact unavailable."
 *   - `audit-limit` — how many of the latest events to show (default 20).
 *
 * Slots:
 *   - `actions` — the action bar.
 */
export class PkKeyDetail extends HTMLElement {
    static readonly tagName = 'pk-key-detail';

    static get observedAttributes(): string[] {
        return ['key', 'impact', 'audit-limit'];
    }

    private readonly _root: ShadowRoot;
    private _key: PkKeyDetailRecord | null | undefined;

    constructor() {
        super();
        this._root = this.attachShadow({ mode: 'open' });
    }

    connectedCallback(): void { this._render(); }

    attributeChangedCallback(name: string): void {
        if (name === 'key') this._key = undefined;
        if (this.isConnected) this._render();
    }

    /** The key (property wins over the attribute once set). */
    get key(): PkKeyDetailRecord | null {
        return this._key !== undefined ? this._key : parseJsonRecord<PkKeyDetailRecord>(this.getAttribute('key'));
    }

    set key(value: PkKeyDetailRecord | null) {
        this._key = value ?? null;
        if (this.isConnected) this._render();
    }

    private _rows(k: PkKeyDetailRecord): Array<[string, string, string]> {
        const rows: Array<[string, string, string]> = [
            ['id', 'Id', k.id],
            ['kind', 'Kind', kindLabel(k.kind)],
            ['custody', 'Custody', custodyLabel(k.custody)],
            ['state', 'State', stateLabel(k.state)],
        ];
        const add = (field: string, label: string, value: string | null | undefined) => { if (value) rows.push([field, label, value]); };
        add('thumbprint', 'Thumbprint', k.thumbprint);
        add('subject', 'Subject', k.subject);
        add('hostname', 'Host', k.hostname);
        add('not-after', 'Expires', k.notAfter ? formatUtc(k.notAfter) : null);
        add('data-class', 'Data class', k.dataClass);
        add('epoch', 'Epoch', k.epoch === null || k.epoch === undefined ? null : String(k.epoch));
        add('wraps', 'Wrapped by', k.wrapsKeyId);
        add('created', 'Created', k.createdAtUtc ? formatUtc(k.createdAtUtc) : null);
        add('retired', 'Retired', k.retiredAtUtc ? formatUtc(k.retiredAtUtc) : null);
        add('disabled', 'Disabled', k.disabledAtUtc ? formatUtc(k.disabledAtUtc) : null);
        add('revoked', 'Revoked', k.revokedAtUtc ? formatUtc(k.revokedAtUtc) : null);
        add('destroyed', 'Destroyed', k.destroyedAtUtc ? formatUtc(k.destroyedAtUtc) : null);
        rows.push(['escrow', 'Escrow copy', k.hasEscrow ? 'Stored (encrypted with your recovery password)' : 'None']);
        return rows;
    }

    private template(): TemplateResult {
        const k = this.key;
        if (!k) return html`<style>${styles}</style><p class="missing">Key not found.</p><slot name="actions"></slot>`;
        const impact = parseJsonRecord<PkKeyImpact>(this.getAttribute('impact'));
        const limit = Math.max(0, Math.trunc(Number(this.getAttribute('audit-limit') ?? '20')) || 0);
        const audit = (k.audit ?? []).slice(-limit);
        return html`
            <style>${styles}</style>
            <header>
                <h2 class="name">${keyName(k)}</h2>
                <pk-status-pill class="custody" kind=${k.custody === 'provider' ? 'info' : 'neutral'}>${custodyLabel(k.custody)}</pk-status-pill>
                <pk-status-pill class="state" kind=${stateKind(k.state)} dot>${stateLabel(k.state)}</pk-status-pill>
            </header>

            <pk-key-value-grid class="meta">
                ${this._rows(k).map(([field, label, value]) => html`<dt>${label}</dt><dd data-field=${field}>${value}</dd>`)}
            </pk-key-value-grid>

            <section class="impact">
                <h3>What this key protects</h3>
                ${impact
                    ? html`
                        <p class="impact-summary">${impactSummary(impact)}</p>
                        ${impact.oldestUtc || impact.newestUtc
                            ? html`<p class="impact-range">From ${formatUtc(impact.oldestUtc)} to ${formatUtc(impact.newestUtc)}.</p>`
                            : nothing}`
                    : html`<p class="impact-summary missing">Impact unavailable.</p>`}
            </section>

            <div class="actions"><slot name="actions"></slot></div>

            <section class="audit">
                <h3>Recent activity</h3>
                ${audit.length === 0
                    ? html`<p class="empty">No events yet.</p>`
                    : html`
                        <table>
                            <thead><tr><th scope="col">When</th><th scope="col">Event</th><th scope="col">By</th><th scope="col">Detail</th></tr></thead>
                            <tbody>
                                ${audit.map((e) => html`
                                    <tr class="event" data-type=${e.type}>
                                        <td class="at">${formatUtc(e.atUtc)}</td>
                                        <td class="type">${e.type}</td>
                                        <td class="actor">${e.actor}</td>
                                        <td class="detail">${e.detail}</td>
                                    </tr>
                                `)}
                            </tbody>
                        </table>
                    `}
            </section>
        `;
    }

    private _render(): void {
        render(this.template(), this._root);
    }
}

if (!customElements.get(PkKeyDetail.tagName)) {
    customElements.define(PkKeyDetail.tagName, PkKeyDetail);
}

declare global {
    interface HTMLElementTagNameMap {
        'pk-key-detail': PkKeyDetail;
    }
}
